import React, { useEffect, useState } from 'react';
import fetcher from '../../../utils/fetcher';
import useAlert from '../../../../../app/src/contexts/alert';
import {
  appToReact,
  AdminDataUnit,
  reactToApp
} from '../shared/configConverter';
import { SetState } from '../../../utils/ISetState';
import AdminDataSection from '../shared/dataBlock';

interface Props {
  handleAxiosError: (err: any) => any;
  className: string;
}

const ControlPanelConfig = ({ handleAxiosError, className }: Props) => {
  const alert = useAlert();
  const [liveConfig, setLiveConfig] = useState<AdminDataUnit[]>([]);
  const [appConfig, setAppConfig] = useState<AdminDataUnit[]>([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [appLoading, setAppLoading] = useState(false);

  useEffect(() => {
    fetchConfig(fetcher.config.getLiveConfig, setLiveConfig);
    fetchConfig(fetcher.config.getAppConfig, setAppConfig);
  }, []);

  const fetchConfig = (
    method: typeof fetcher.config.getAppConfig,
    setConfig: SetState<AdminDataUnit[]>
  ) => {
    method()
      .then((response) => {
        setConfig(appToReact(response.config, true));
      })
      .catch(handleAxiosError);
  };

  const handleLiveChange = (unit: AdminDataUnit, newValue: any) => {
    handleChange(unit, newValue, setLiveConfig);
  };

  const handleAppChange = (unit: AdminDataUnit, newValue: any) => {
    handleChange(unit, newValue, setAppConfig);
  };

  const handleChange = (
    unit: AdminDataUnit,
    newValue: any,
    setter: SetState<AdminDataUnit[]>
  ) => {
    setter((prev) =>
      prev.map((prevUnit) => {
        if (prevUnit.key === unit.key) {
          return { ...prevUnit, value: newValue };
        }
        return prevUnit;
      })
    );
  };

  const saveLive = () => {
    setLiveLoading(true);
    fetcher.config
      .setLiveConfig(reactToApp(liveConfig))
      .then((response) => {
        setLiveConfig(appToReact(response.config, true));
        if (response.appError) {
          alert.show("Config is updated, but app didn't catch the update");
        } else {
          alert.show('👌');
        }
      })
      .catch(handleAxiosError)
      .finally(() => setLiveLoading(false));
  };

  const saveApp = () => {
    setAppLoading(true);
    const appFormat = reactToApp(appConfig);
    fetcher.config
      .setAppConfig(appFormat)
      .then(() => {
        setAppConfig(appToReact(appFormat, true));
        alert.show('👌');
      })
      .catch(handleAxiosError)
      .finally(() => setAppLoading(false));
  };

  return (
    <div className={className}>
      <AdminDataSection
        data={liveConfig}
        handleChange={handleLiveChange}
        header='Live settings'
        description='These settings are applied immediately (no server restart required)'
        button={{
          onClick: saveLive,
          title: 'Apply Changes',
          loading: liveLoading
        }}
      />
      <AdminDataSection
        data={appConfig}
        handleChange={handleAppChange}
        header='Startup settings'
        description='These settings will be applied only after server restart'
        button={{
          onClick: saveApp,
          title: 'Save Changes',
          loading: appLoading
        }}
      />
    </div>
  );
};

export default ControlPanelConfig;
